import * as React from 'react';
import * as _ from 'lodash';
import * as qs from 'query-string';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { AppActionCreators, IApplicationState, ReportActionCreators, ICprScoreAuditResponse } from '@nkr/store/index';
import { ICenterBase, ICprScoreAudit } from '@nkr/models/index';
import { ListGroup, ListGroupItem, Panel, Glyphicon } from 'react-bootstrap';
import Grid from './grid';
import Header from './header';
import { useEffect, useReducer } from 'react';
import { initialState, reducer } from './reducer';

interface IProps {
    centerId: number,
    isAdmin: boolean
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCprAuditScores: (centerId: number, startDate: string, endDate: string) => Promise<ICprScoreAuditResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const CprScoreAuditReport = (props: TProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        getData(`Getting CPR Scores, one moment please...`);
    }, []);

    useEffect(() => {
        if (!state.initialLoad) {
            refreshData(`Getting CPR Scores, one moment please...`, props.centerId, state.reportStart, state.reportEnd);
        }
    }, [state.reportStart, state.reportEnd]);

    useEffect(() => {
        setUrlParameters();
    }, [state.reportStart, state.reportEnd, state.included, state.compatible, state.transplanted]);

    const getData = (message: string) => {
        let { centerId } = props;

        let parsed = qs.parse(location.search);

        let startDate = moment(parsed?.startDate, 'YYYY-MM-DD', true);
        let endDate = moment(parsed?.endDate, 'YYYY-MM-DD', true);

        if (!startDate.isValid()) {
            startDate = moment().startOf('month').subtract(6, 'months');
        }

        if (!endDate.isValid()) {
            endDate = moment(state.reportEnd, 'YYYY-MM-DD', true);
        }

        let included = true;
        let compatible = false;
        let transplanted: boolean = null;

        if (parsed?.included) {
            included = parseInt(parsed.included) == 1;
        }

        if (parsed?.compatible) {
            compatible = parseInt(parsed.compatible) == 1;
        }

        if (parsed?.transplanted) {
            transplanted = parseInt(parsed.transplanted) == 1;
        }

        let startDateStr = startDate.format('YYYY-MM-DD');
        let endDateStr = endDate.format('YYYY-MM-DD');

        props.showLoading(message).then(() => {
            props.getCprAuditScores(centerId, startDateStr, endDateStr).then(response => {
                let { reportData: data, center, reportStart, reportEnd } = response.data.results;

                dispatch({
                    type: 'afterGetData',
                    payload: {
                        data,
                        center,
                        reportStart,
                        newReportStart: reportStart,
                        reportEnd,
                        newReportEnd: reportEnd,
                        included,
                        compatible,
                        transplanted
                    }
                });

                props.hideLoading();
            });
        });
    };

    const refreshData = (message: string, centerId, startDate, endDate) => {
        props.showLoading(message).then(() => {
            props.getCprAuditScores(centerId, startDate, endDate).then(response => {
                let { reportData: data, center, reportStart, reportEnd } = response.data.results;

                dispatch({
                    type: 'refreshData',
                    payload: {
                        data,
                        center,
                        reportStart,
                        reportEnd,
                    }
                });

                props.hideLoading();
            });
        });
    };

    const setUrlParameters = () => {
        let paramList = [];

        paramList.push(encodeURI('startDate=' + state.reportStart));
        paramList.push(encodeURI('endDate=' + state.reportEnd));
        paramList.push(encodeURI('included=' + (state.included ? '1' : '0')));
        paramList.push(encodeURI('compatible=' + (state.compatible ? '1' : '0')));

        if (state.transplanted != null) {
            paramList.push(encodeURI('transplanted=' + (state.transplanted ? '1' : '0')));
        }

        let params = paramList.join('&');

        let newUrl = '/center/' + props.centerId + '/reports/my-center/cpr-score-audit-report?' + params

        window.history.pushState({}, '', newUrl);
    };

    const getFilteredData = () => {
        return state.data.filter(x => {
            let filter = true;

            if (state.included && !state.compatible) {
                filter = x.isIncluded;
            }

            if (!state.included && state.compatible) {
                filter = x.isCompatiblePair;
            }

            if (state.included && state.compatible) {
                filter = x.isIncluded || x.isCompatiblePair;
            }

            if (state.transplanted != null) {
                filter = filter && x.isTranplanted == state.transplanted && moment(x.transplantDate) > moment(state.reportStart);
            }

            return filter;
        });
    };

    const handleFilterToggle = (name: string, value: boolean) => {
        dispatch({
            type: 'updateField',
            payload: {
                name,
                value
            }
        });
    };

    const handleCompatibleTotalClick = () => {
        dispatch({
            type: 'compatibleOrIncludedTotalOnly',
            payload: {
                included: false,
                compatible: true,
                transplanted: null
            }
        });
    };

    const handleIncludedTotalClick = () => {
        dispatch({
            type: 'compatibleOrIncludedTotalOnly',
            payload: {
                included: true,
                compatible: false,
                transplanted: null
            }
        })
    };

    const handleReportStartChange = (newReportStart: string) => {
        dispatch({
            type: 'updateField',
            payload: {
                name: 'newReportStart',
                value: newReportStart
            }
        });
    };

    const handleReportEndChange = (newReportEnd: string) => {
        dispatch({
            type: 'updateField',
            payload: {
                name: 'newReportEnd',
                value: newReportEnd
            }
        });
    };

    const handleRefresh = () => {
        dispatch({
            type: 'updateDateRange',
            payload: {
                reportStart: state.newReportStart,
                reportEnd: state.newReportEnd
            }
        });
    };

    const handleToggleHelp = () => {
        dispatch({
            type: 'updateField',
            payload: {
                name: 'expandHelp',
                value: !state.expandHelp
            }
        });
    };

    const centerAlias = state.center != null ? state.center.alias : '';
    const data = getFilteredData();
    const txpIncluded = data.filter(x => x.isIncluded).length;
    const txpCompatible = data.filter(x => x.isCompatiblePair && x.isIncluded && (state.transplanted != null ? x.isTranplanted == state.transplanted : true)).length;
    const txpCount = data.filter(x=>x.isTranplanted).length;
    const cprScore = Math.floor(txpIncluded > 0 ? (txpCompatible / txpIncluded) * 100 : 0);

    return (
        <>
            {props.isAdmin && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 28, fontWeight: 500 }}>{centerAlias} CPR Audit - 6 months</div>
                    <a href={'/report/cpr-report'}>Return to CPR Score Report</a>
                </div>
            )}

            <Header
                data={state.data}
                reportStart={state.newReportStart}
                reportEnd={state.newReportEnd}
                included={state.included}
                compatible={state.compatible}
                transplanted={state.transplanted}
                txpIncluded={txpIncluded}
                txpCompatible={txpCompatible}
                cprScore={cprScore}
                onReportStartChange={handleReportStartChange}
                onReportEndChange={handleReportEndChange}
                onReportRangeUpdate={handleRefresh}
                onFilterToggle={handleFilterToggle}
                onCompatibleTotalOnly={handleCompatibleTotalClick}
                onIncludedTotalOnly={handleIncludedTotalClick}
            />

            <Panel style={{ marginBottom: 5 }}>
                <Panel.Heading>
                    <Panel.Toggle>Some helpful information...</Panel.Toggle>
                    <Panel.Toggle componentClass='a' className='pull-right' style={{ cursor: 'pointer' }}><Glyphicon glyph={state.expandHelp ? 'triangle-bottom' : 'triangle-top'} /> {state.expandHelp ? 'Show' : 'Hide'}</Panel.Toggle>
                </Panel.Heading>
                <Panel.Collapse onEnter={handleToggleHelp} onExit={handleToggleHelp}>
                    <ListGroup style={{ marginBottom: 0 }}>
                        <ListGroupItem>
                            <div style={{ fontWeight: 550 }}>Included Transplants</div>
                            <div style={{ paddingLeft: 20 }}>All non-waitlist chain end transplants (Paired, Voucher, Remote Direct, NKR Internal Direct, and Internal Directs with Microsites.)</div>
                        </ListGroupItem>
                        <ListGroupItem>
                            <div style={{ fontWeight: 550 }}>Compatible Pair Ratio (CPR)</div>
                            <div style={{ paddingLeft: 20 }}>Calculated as a 6-month moving average.</div>
                        </ListGroupItem>
                        <ListGroupItem>
                            <div style={{ fontWeight: 550 }}>Formula for CPR Result</div>
                            <div style={{ paddingLeft: 20, width: 'fit-content' }}>
                                <div style={{ borderBottom: '1px solid black' }}>Compatible, Paired & Voucher Transplants</div>
                                <div style={{ textAlign: 'center' }}>Included Transplants</div>
                            </div>
                        </ListGroupItem>
                        <ListGroupItem>
                            <div style={{ fontWeight: 550 }}>Report Range</div>
                            <div style={{ paddingLeft: 20 }}>Uses invoice date to account for lag reporting on direct transplant cases.</div>
                            <div style={{ paddingLeft: 20 }}>When the 'transplanted' toggle is used, then transplanted date is used to further filter the data.</div>
                        </ListGroupItem>
                    </ListGroup>
                </Panel.Collapse>
            </Panel>

            <Grid
                data={data}
                isAdmin={props.isAdmin}
                compatibleCount={txpCompatible}
                includedCount={txpIncluded}
                transplantedCount={txpCount}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCprAuditScores: (centerId, startDate, endDate) => {
            return dispatch(ReportActionCreators.getCprScoreAudit(centerId, startDate, endDate));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CprScoreAuditReport);